import { Flex, Modal, ScrollArea, Text } from "@mantine/core";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import CommentInput from "src/components/pages/Summary/CommentsModal/CommentInput";
import { useGetApi, usePostApi } from "src/hooks";
import { type IComment } from "src/pages/Summary/Summary.types";
import { useSelector } from "src/redux/hooks";

interface Props {
  opened: boolean;
  close: () => void;
}

const ActionRequired = ({ opened, close }: Props) => {
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const auditor = useSelector(state => state.permsSlice.auditor);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { post, isLoading } = usePostApi();
  const { aid, idType, lbeidOrPartId } = useParams();

  const mapReviewComments = useCallback(
    (data: any) =>
      (auditor ? data.data.audit : data.data.reverify) as IComment[],
    [auditor],
  );

  const { data, setData } = useGetApi({
    request: "get-review-reverify-comment",
    body: {
      params: {
        aid,
        [String(idType)]: lbeidOrPartId,
      },
    },
    mapper: mapReviewComments,
  });

  useEffect(() => {
    setTimeout(() => {
      if (opened && scrollRef.current && data && data.length > 0) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "instant",
        });
      }
    }, 100);
  }, [data, opened]);

  const handleSend = useCallback(
    async (message: string) => {
      const body: any = { params: { aid, message } };
      body.params[String(idType)] = lbeidOrPartId;
      await post({
        request: "send-review-reverify-comment",
        body,
        onSuccess: res => {
          setData(auditor ? res.data.audit : res.data.reverify);
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({
              top: scrollRef.current.scrollHeight,
              behavior: "smooth",
            });
          });
        },
      });
    },
    [aid, idType, lbeidOrPartId, post, setData, auditor],
  );

  return (
    <Modal
      title={auditor ? "Audit" : "Re-Verify"}
      opened={opened}
      onClose={close}>
      <ScrollArea
        viewportRef={scrollRef}
        my={20}
        type="auto"
        h="40vh"
        offsetScrollbars>
        <Flex direction="column" gap={12} px={12}>
          {data?.map(comment => (
            <Flex
              key={`${comment.date}${comment.message}`}
              direction="column"
              p={10}
              bg="blue.9"
              gap={8}
              maw="70%"
              style={{
                borderRadius: 12,
              }}>
              <Flex justify="space-between">
                <Text size="xs" c="white">
                  {comment.from ?? `${comment.fname} ${comment.sname}`}
                </Text>
                <Text size="xs" c="white">
                  {dayjs(comment.date).format("D MMM YYYY - HH:mm")}
                </Text>
              </Flex>
              <Text c="white" style={{ borderRadius: 12 }}>
                {uppercase && comment.message
                  ? comment.message.toUpperCase()
                  : comment.message}
              </Text>
            </Flex>
          ))}
        </Flex>
      </ScrollArea>

      <CommentInput onSend={handleSend} isLoading={isLoading} />
    </Modal>
  );
};

export default ActionRequired;
